export function normalizeOfferId(offerId: string | null | undefined): string {
  if (!offerId) return "";
  return offerId.replace(/_None_/g, "_0_").replace(/_None$/, "_0");
}

export interface ParsedOfferId {
  type: string;
  duration: number;
  apr: number;
  downPayment: number | null;
  promoDuration: number | null;
  promoApr: number | null;
  fullId: string;
}

export function parseOfferId(offerId: string): ParsedOfferId | null {
  if (!offerId) return null;

  const normalized = normalizeOfferId(offerId);
  const parts = normalized.split("_");

  if (parts.length < 4) return null;

  const type =
    parts[0] === "FIXED" && parts[1] === "APR"
      ? "FIXED_APR"
      : parts[0] === "SAC"
        ? "SAC"
        : parts.slice(0, 2).join("_");
  const typeOffset = type === "FIXED_APR" ? 2 : 1;

  const durationStr = parts[typeOffset] || "";
  const duration = parseInt(durationStr.replace("m", ""), 10) || 0;

  const apr = parseFloat(parts[typeOffset + 1] || "0") || 0;

  const downPaymentStr = parts[typeOffset + 2] || "0";
  const downPayment =
    downPaymentStr === "0" ? 0 : downPaymentStr ? parseInt(downPaymentStr, 10) || null : null;

  const promoDurationStr = parts[typeOffset + 3] || "0m";
  const promoDurationParsed = parseInt(promoDurationStr.replace("m", ""), 10);
  const promoDuration = isNaN(promoDurationParsed) ? null : promoDurationParsed;

  const promoAprStr = parts[typeOffset + 4];
  const promoApr = promoAprStr !== undefined ? parseFloat(promoAprStr) : null;

  return {
    type,
    duration,
    apr,
    downPayment,
    promoDuration,
    promoApr,
    fullId: normalized,
  };
}

export function formatOfferIdShort(offerId: string): string {
  const parsed = parseOfferId(offerId);
  if (!parsed) return offerId;

  return `${parsed.type}_${parsed.duration}m`;
}

export function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export function formatDollars(dollars: number | null | undefined): string {
  if (dollars === null || dollars === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(dollars);
}

export function centsToDollars(cents: number | null | undefined): number {
  if (cents === null || cents === undefined) return 0;
  return cents / 100;
}

export function dollarsToCents(dollars: number | null | undefined): number {
  if (dollars === null || dollars === undefined || isNaN(dollars)) return 0;
  return Math.round(dollars * 100);
}

function parseDate(date: string | Date): Date {
  if (date instanceof Date) return date;

  if (typeof date === "string") {
    if (date.includes("Z") || /[+-]\d{2}:\d{2}$/.test(date)) {
      return new Date(date);
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(date)) {
      return new Date(date + "Z");
    }
  }

  return new Date(date);
}

export function formatDate(date: string | Date): string {
  const d = parseDate(date);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatDateShort(date: string | Date): string {
  const d = parseDate(date);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function formatRelativeTime(date: string | Date): string {
  const d = parseDate(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(d);
}

export function formatDateForInput(date: Date | undefined): string {
  if (!date) return "";
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
}

export function formatDateTimeFull(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = parseDate(date);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");
  const displaySeconds = seconds.toString().padStart(2, "0");
  return `${month} ${day}, ${year}, ${displayHours}:${displayMinutes}:${displaySeconds} ${ampm}`;
}
